import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { getIdData, addData, updateData } from '../../lib/studentprogresstask'
import { TaskTopic911Select, TaskTopic12Select } from '../Common/FormSelects'
import { getStudentClass } from '../../lib/classdetailview'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { useLayout } from '../../Hooks/useLayout'
import { Card, CardBody, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { Breadcrumbs } from '../../AbstractElements'
import HeaderCard from '../Common/Component/HeaderCard'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const getCurrentDateInput = () => {
	const today = new Date()
	return today.toISOString().split('T')[0]
}

const InstructorStudentTaskEdit = () => {
	const { status } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const role = useRoleBasedUrl()
	const layout = useLayout()

	const queryParams = new URLSearchParams(location.search)
	const studentId = queryParams.get('studentId')
	const type = queryParams.get('type')

	const isEdit = status?.toLowerCase() !== 'add'

	const [taskTopic, setTaskTopic] = useState('')
	const [assignmentText, setAssignmentText] = useState('')
	const [assignedDate, setAssignedDate] = useState(getCurrentDateInput())
	const [dueDate, setDueDate] = useState(getCurrentDateInput())
	const [studentClass, setStudentClass] = useState()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	useEffect(() => {
		if (isEdit) {
			loadTaskData()
		}
		getStudentClassData()
	}, [status])

	if (!studentId || !type) {
		return (
			<div className="container-fluid">
				<Alert color="danger" className="m-4">
					<strong>Error:</strong> studentId or type parameter is missing! Please go back and try again.
				</Alert>
			</div>
		)
	}

	const getStudentClassData = async () => {
		try {
			const classInfo = await getStudentClass(studentId)
			if (classInfo && classInfo.length > 0) {
				setStudentClass(classInfo[0].grade)
			}
		} catch (error) {
			console.error('Failed to fetch student class:', error)
		}
	}

	const loadTaskData = async () => {
		try {
			const data = await getIdData(status)
			const task = data[0]

			if (task) {
				setAssignmentText(task.taskname)
				setAssignedDate(task.taskdate)
				setDueDate(task.taskenddate)
				setTaskTopic(task.subject || '')
			} else {
				console.warn('Task not found.')
			}
		} catch (error) {
			console.error('Failed to load task:', error)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		const payload = {
			studentId,
			taskname: assignmentText,
			taskdate: assignedDate,
			taskenddate: dueDate,
			isFinish: false,
			progresstype: type,
			subject: taskTopic
		}

		try {
			if (isEdit) {
				await updateData(status, payload)
			} else {
				await addData(payload)
			}
			
			showToastMessage(isEdit ? 'Task updated successfully!' : 'Task added successfully!')
			setTimeout(() => {
				navigate(`${process.env.PUBLIC_URL}/${role}/student-tasks/${studentId}/${layout}?collapse=open`)
			}, 2000)
		} catch (error) {
			console.error('Failed to save task:', error)
			setError('An error occurred while saving the task.')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/student-tasks/${studentId}/${layout}`)
	}

	return (
		<>
			<div className="position-relative">
				<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			</div>
			<Breadcrumbs parent="Student Management" title={isEdit ? 'Update Task' : 'Add New Task'} mainTitle={isEdit ? 'Update Task' : 'Add New Task'} />
			<div className="container-fluid">
				<Row>
					<Col sm="12">
						<Card>
							<HeaderCard
								title={isEdit ? 'Update Task' : 'Add New Task'}
								subTitle={isEdit ? 'Edit existing student task' : 'Create a new task for student'}
							/>
							<CardBody>
								{error && (
									<Alert color="danger" className="alert-dismissible fade show" role="alert">
										<strong>Error:</strong> {error}
										<Button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></Button>
									</Alert>
								)}

								<form onSubmit={handleSubmit}>
									<Row>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Topic</Label>
												{studentClass > 11 ? (
													<TaskTopic12Select value={taskTopic} setValue={setTaskTopic} />
												) : (
													<TaskTopic911Select value={taskTopic} setValue={setTaskTopic} />
												)}
											</FormGroup>
										</Col>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Task</Label>
												<Input
													type="text"
													value={assignmentText}
													onChange={(e) => setAssignmentText(e.target.value)}
													placeholder="Enter task description"
													required
													disabled={loading}
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Assignment Date</Label>
												<Input type="date" value={assignedDate} onChange={(e) => setAssignedDate(e.target.value)} disabled={loading} />
											</FormGroup>
										</Col>
										<Col lg="6">
											<FormGroup>
												<Label className="form-control-label">Due Date</Label>
												<Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={loading} />
											</FormGroup>
										</Col>
									</Row>

									<div className="d-flex justify-content-between mt-4">
										<Button type="submit" color="primary" disabled={loading}>
											{loading ? 'Saving...' : isEdit ? 'Update Task' : 'Add Task'}
										</Button>
										<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
											Cancel
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</div>
		</>
	)
}

export { InstructorStudentTaskEdit }
